import * as THREE from "three";
import { sceneOrientations } from "../../../constants";
import { PLANE } from "./consts";
import {
    computeBoundsTree,
    disposeBoundsTree,
    acceleratedRaycast,
} from "three-mesh-bvh";

THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;
let defaultRaycast = THREE.Mesh.prototype.raycast;
THREE.Mesh.prototype.raycast = acceleratedRaycast;

export function isPointInsideMRIVolume(queryPt, mriModel) {
    return mriModel.boundingBox.containsPoint(queryPt);
}

export function getAdjacencyListFromMesh(faces) {
    const neighbors = {};
    for (let i = 0; i < faces.length; i += 3) {
        let a = faces[i + 0];
        let b = faces[i + 1];
        let c = faces[i + 2];

        neighbors[parseInt(a)] = neighbors[parseInt(a)]
            ? neighbors[parseInt(a)].add(b).add(c)
            : new Set().add(b).add(c);
        neighbors[parseInt(b)] = neighbors[parseInt(b)]
            ? neighbors[parseInt(b)].add(c).add(a)
            : new Set().add(c).add(a);
        neighbors[parseInt(c)] = neighbors[parseInt(c)]
            ? neighbors[parseInt(c)].add(a).add(b)
            : new Set().add(a).add(b);
    }

    return neighbors;
}

export function getRaycasterIntersectionWithPlane(raycaster, clipPlane) {
    let intersect = new THREE.Vector3();
    raycaster.ray.intersectPlane(clipPlane, intersect);
    return intersect;
}

export function getRaycasterIntersections(raycaster, objectArray) {
    let intersects = [];

    for (const i of objectArray) {
        i.raycast(raycaster, intersects);
    }

    if (intersects && intersects.length > 0) {
        intersects.sort((a, b) => a.distance - b.distance);
    }

    return intersects;
}

export function getRaycasterFirstIntersectionBehindClipPlane(
    raycaster,
    objectArray,
    clipPlane
) {
    let intersects = [];

    for (const i of objectArray) {
        i.raycast(raycaster, intersects);
    }

    if (intersects) {
        intersects.sort((a, b) => a.distance - b.distance);

        for (let i = intersects.length - 1; i >= 0; i--) {
            let dist2Plane = clipPlane.distanceToPoint(
                intersects[Number(i)].point
            );

            if (
                dist2Plane <= 0 &&
                clipPlane.intersectsBox(
                    intersects[Number(i)].StructureModel.boundingBox
                )
            ) {
                return intersects[Number(i)].StructureModel;
            }
        }
    }

    return null;
}

export function getPointProjectedOntoPlane(point, plane) {
    let projection = new THREE.Vector3();

    if (plane) {
        plane.projectPoint(point, projection);
    }

    return projection;
}

export function computeCancerLesionOverlap(
    cancerLesion,
    cancerLesionBounds,
    tools,
    subdivisions,
    cache = {},
    useDefaultRaycast = false
) {
    if (tools.length === 0) return 0;

    if (useDefaultRaycast) THREE.Mesh.prototype.raycast = defaultRaycast;

    let isCacheValid = cache["lesionUUID"] === cancerLesion.uuid;

    if (!isCacheValid) initializeCache(cache, cancerLesion.uuid);

    let emptyRaycasts = cache["emptyRaycasts"];
    let bboxes = cache["bboxes"];
    let exclusivelyIntraLesionCellCoverages =
        cache["exclusivelyIntraLesionCellCoverages"];

    let intersectionScene = [cancerLesion, ...tools];

    let testPoints = [];
    if (isCacheValid) {
        _pushDirtyPoints(
            tools,
            bboxes,
            cancerLesionBounds,
            subdivisions,
            emptyRaycasts,
            testPoints
        );
    } else {
        _pushAllPoints(subdivisions, cancerLesionBounds, testPoints);
    }

    for (let point of testPoints) {
        let ray = new THREE.Raycaster();
        ray.set(point, new THREE.Vector3(0, 0, -1));
        let intersects = ray.intersectObjects(intersectionScene);

        intersects = removeDuplicateIntersections(intersects);

        let [cellTotalLesionSize, cellExclusivelyIntraLesionSize] =
            _measureCoverageForCell(intersects, cancerLesion, tools);

        // Total lesion size only needs to be computed once
        if (!isCacheValid) {
            cache["totalLesionSize"] += cellTotalLesionSize;
        }

        exclusivelyIntraLesionCellCoverages.set(
            _vectorToString(point),
            cellExclusivelyIntraLesionSize
        );

        if (cellTotalLesionSize === 0) {
            emptyRaycasts.add(point.toArray().toString());
        }
    }

    let exclusivelyIntraLesionSize = 0;
    for (let size of exclusivelyIntraLesionCellCoverages.values()) {
        exclusivelyIntraLesionSize += size;
    }

    // Otherwise the raycast function stays switched
    if (useDefaultRaycast) THREE.Mesh.prototype.raycast = acceleratedRaycast;

    return 1 - exclusivelyIntraLesionSize / cache["totalLesionSize"];
}

function _pushAllPoints(subdivisions, cancerLesionBounds, testPoints) {
    for (let i = 0; i < subdivisions; i++) {
        for (let j = 0; j < subdivisions; j++) {
            let point = _getPoint(cancerLesionBounds, subdivisions, i, j);
            testPoints.push(point);
        }
    }
}

function _pushDirtyPoints(
    tools,
    bboxes,
    cancerLesionBounds,
    subdivisions,
    emptyRaycasts,
    testPoints
) {
    let pointSet = new Set();
    let checkedTools = new Set();

    for (let tool of tools) {
        checkedTools.add(tool.uuid);

        let toolBounds = _getToolBounds(tool);

        if (bboxes.has(tool.uuid)) {
            let cachedToolBounds = bboxes.get(tool.uuid);

            if (
                _getBoundingBoxAsString(toolBounds) !==
                _getBoundingBoxAsString(cachedToolBounds)
            ) {
                // Add all points of new and old bounding box
                addTestPointsToSet(
                    cachedToolBounds,
                    cancerLesionBounds,
                    subdivisions,
                    emptyRaycasts,
                    pointSet
                );
                addTestPointsToSet(
                    toolBounds,
                    cancerLesionBounds,
                    subdivisions,
                    emptyRaycasts,
                    pointSet
                );

                bboxes.set(tool.uuid, toolBounds);
            }
        } else {
            addTestPointsToSet(
                toolBounds,
                cancerLesionBounds,
                subdivisions,
                emptyRaycasts,
                pointSet
            );
            bboxes.set(tool.uuid, toolBounds);
        }
    }

    // Check for any bounding boxes that have been deleted
    for (let [uuid, toolBounds] of bboxes) {
        if (!checkedTools.has(uuid)) {
            addTestPointsToSet(
                toolBounds,
                cancerLesionBounds,
                subdivisions,
                emptyRaycasts,
                pointSet
            );
            bboxes.set(uuid, toolBounds);
        }
    }

    // Convert back vector strings to three.js vectors
    pointSet.forEach((point) => testPoints.push(_stringToVector(point)));
}

function initializeCache(cache, uuid) {
    cache["emptyRaycasts"] = new Set();
    cache["bboxes"] = new Map();
    cache["lesionUUID"] = uuid;
    cache["exclusivelyIntraLesionCellCoverages"] = new Map();
    cache["totalLesionSize"] = 0;
}

function _vectorToString(vector) {
    return vector.toArray().toString();
}

function _stringToVector(string) {
    let components = string.split(",");
    let x = parseFloat(components[0]);
    let y = parseFloat(components[1]);
    let z = parseFloat(components[2]);
    return new THREE.Vector3(x, y, z);
}

function _getPoint(cancerLesionBounds, subdivisions, i, j) {
    let x =
        cancerLesionBounds.min.x +
        ((cancerLesionBounds.max.x - cancerLesionBounds.min.x) / subdivisions) *
            i;

    let y =
        cancerLesionBounds.min.y +
        ((cancerLesionBounds.max.y - cancerLesionBounds.min.y) / subdivisions) *
            j;

    let z = cancerLesionBounds.max.z;

    let point = new THREE.Vector3(x, y, z);

    return point;
}

function addTestPointsToSet(
    bounds,
    outerBounds,
    subdivisions,
    ignoreList,
    pointSet
) {
    let points = [];

    for (let i = 0; i < subdivisions; i++) {
        for (let j = 0; j < subdivisions; j++) {
            let point = _getPoint(outerBounds, subdivisions, i, j);

            if (
                point.x > bounds.min.x &&
                point.x < bounds.max.x &&
                point.y > bounds.min.y &&
                point.y < bounds.max.y
            )
                points.push(point);
        }
    }

    points.forEach((point) => {
        if (!ignoreList.has(_vectorToString(point))) {
            pointSet.add(_vectorToString(point));
        }
    });
}

function _getToolBounds(object) {
    let toolMeshes = object.children.filter((child) => child.type === "Mesh");
    return new THREE.Box3().setFromObject(toolMeshes[0]);
}

function _getBoundingBoxAsString(bbox) {
    return _vectorToString(bbox.min) + _vectorToString(bbox.max);
}

function _measureCoverageForCell(intersects, cancerLesion, tools) {
    // Iterate over intersects adding segments of the ray that are
    // both inside the cancer lesion and not inside a target. The total is
    // the uncovered cancer lesion size.
    // At the same time, keep track of total cancer lesion length, then
    // compute their relative percentages.
    let exclusivelyIntraLesionSize = 0;
    let lesionSize = 0;

    let insideLesion = false;
    let insideTarget = new Set();

    let lastLesionPosition;
    for (let [index, intersect] of intersects.entries()) {
        // We either hit the front face or back face of an object.
        // The individual faces are children, therefore we take the parent object.
        let parentObject = intersect.object.parent;

        if (parentObject.uuid === cancerLesion.uuid) {
            insideLesion = !insideLesion;

            if (insideLesion) {
                lastLesionPosition = intersect.distance;
            } else {
                lesionSize += Math.abs(intersect.distance - lastLesionPosition);
            }
        }

        if (tools.includes(parentObject)) {
            if (insideTarget.has(parentObject))
                insideTarget.delete(parentObject);
            else insideTarget.add(parentObject);
        }

        if (insideLesion && insideTarget.size === 0) {
            // Handle a case where the duplicate search missed a duplicate
            // intersection due to floating point rounding error.
            if (!intersects[index + 1]) continue;

            exclusivelyIntraLesionSize += Math.abs(
                intersects[index + 1].distance - intersect.distance
            );
        }
    }

    return [lesionSize, exclusivelyIntraLesionSize];
}

export function removeDuplicateIntersections(intersects) {
    // Duplicate intersections arise when a ray intersects the line between
    // two faces. We keep a set of already intersected points for each mesh to
    // avoid duplicates.

    let intersectionSet = {};
    return intersects.filter((intersect) => {
        if (!intersectionSet[intersect.object.uuid]) {
            intersectionSet[intersect.object.uuid] = new Set();
            intersectionSet[intersect.object.uuid].add(
                _vectorToString(intersect.point)
            );
            return true;
        }

        let exists = intersectionSet[intersect.object.uuid].has(
            _vectorToString(intersect.point)
        );
        if (exists) {
            return false;
        } else {
            intersectionSet[intersect.object.uuid].add(
                _vectorToString(intersect.point)
            );
            return true;
        }
    });
}

export function getQuaternionAlignedWithBounds(intersect, boundingCone) {
    let trgtDir = new THREE.Vector3(
        intersect.x - boundingCone.vertex.x,
        intersect.y - boundingCone.vertex.y,
        intersect.z - boundingCone.vertex.z
    );

    let upVec = new THREE.Vector3(0, 1, 0);
    let quaternion = new THREE.Quaternion();
    quaternion.setFromUnitVectors(upVec, trgtDir.clone().normalize());

    return quaternion;
}

export function getPositionTranslatedInPlane(
    currentPosition,
    adjustmentDistance,
    orientation,
    plane
) {
    let updatedPosition = new THREE.Vector3();

    updatedPosition.copy(currentPosition);

    switch (orientation) {
        case sceneOrientations.SAGITTAL:
            if (plane === PLANE.VERTICAL) {
                updatedPosition.z = currentPosition.z + adjustmentDistance;
            } else {
                updatedPosition.y = currentPosition.y - adjustmentDistance;
            }

            break;

        case sceneOrientations.CORONAL:
            if (plane === PLANE.VERTICAL) {
                updatedPosition.z = currentPosition.z + adjustmentDistance;
            } else {
                updatedPosition.x = currentPosition.x - adjustmentDistance;
            }

            break;

        case sceneOrientations.AXIAL:
            if (plane === PLANE.VERTICAL) {
                updatedPosition.y = currentPosition.y - adjustmentDistance;
            } else {
                updatedPosition.x = currentPosition.x - adjustmentDistance;
            }

            break;

        default:
            break;
    }

    return updatedPosition;
}

export const LPS2RAS = new THREE.Matrix4().makeBasis(
    // Shouldn't the Y-axis be negated and not the Z-axis?
    new THREE.Vector3(-1, 0, 0),
    new THREE.Vector3(0, 1, 0),
    new THREE.Vector3(0, 0, -1)
);
// This should just be identical to LPS2RAS
export const RAS2LPS = LPS2RAS.invert();
